import { FunctionComponent, useEffect } from 'react';
import { SearchLandingContext } from 'components/search/SearchLandingContext';
import { SearchNavigation } from 'components/search/SearchNavigation';
import { SearchRedirect } from 'components/search/SearchRedirect';
import { SegmentedSearchHero } from 'components/search/SegmentedSearchHero';
import { WidgetConsumerPageContent } from 'components/www/WidgetConsumerPageContent';
import { core } from '@tock/coretypes';
import { getMetroAreas } from 'selectors/appSelector';
import { useActions } from '@tock/shared/utils/connect';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useHistory, useLocation, useParams } from 'react-router';
import { usePrevious } from '@tock/hooks';
import { useSelector } from 'react-redux';

export const CitySearchLanding: FunctionComponent = () => {
  const currentMetroArea = useCurrentMetroArea();
  const metroAreas = useSelector(getMetroAreas);
  const { citySlug } = useParams<{ citySlug: string }>();
  const { actions } = useActions();
  const history = useHistory();
  const location = useLocation();

  const prevCurrentMetroArea = usePrevious(currentMetroArea);

  useEffect(() => {
    if (
      prevCurrentMetroArea &&
      currentMetroArea &&
      prevCurrentMetroArea.slug !== currentMetroArea.slug &&
      currentMetroArea.slug !== citySlug
    ) {
      // update url if this changes while on the page, usually city picker
      history.push(`/city/${currentMetroArea.slug}?tock_source=tock&tock_medium=search_nav`);
    } else if (currentMetroArea && currentMetroArea.slug !== citySlug) {
      // change the metro area if the current metro is not the city slug in the url
      const newMetroArea = (metroAreas as core.MetroArea[]).find((m) => m.slug === citySlug);
      if (newMetroArea) {
        actions.metroArea.selectMetroArea(newMetroArea);
        // you could have a selected metro area with a slug of 'chicago', then go directly to 'new-york-city' page.
        // we've selected the new metro area now, but we only want to push if this happens and the slug also doesn't match
        if (citySlug !== newMetroArea.slug) {
          history.push(`/city/${newMetroArea.slug}?tock_source=tock&tock_medium=search_nav`);
        }
      } else {
        history.replace('/');
      }
    }
  }, [actions.metroArea, citySlug, currentMetroArea, history, metroAreas, prevCurrentMetroArea]);

  const title = `Let's find you something delicious in ${currentMetroArea?.name ?? ''}`;

  return (
    <>
      <SearchNavigation />
      <SearchRedirect type="city" />
      <SearchLandingContext
        locale={currentMetroArea?.locale}
        currencyCode={currentMetroArea?.currencyCode}
      >
        <div className="PageContent PageContent--page-search">
          <SegmentedSearchHero metroArea={currentMetroArea} title={title} />

          <div className="DiscoverPage-results" data-testid="discoverpage-results">
            <WidgetConsumerPageContent pathname={location.pathname} standAlone={false} />
          </div>
        </div>
      </SearchLandingContext>
    </>
  );
};
